import type {SocialNetworkSource} from '@src/Store/Slices/AppSlice/auth.slice';

const DEFAULT_PROVIDERS: SocialNetworkSource[] = [
  'google',
  'apple',
  'facebook',
  'email',
];

const VODACOM_ONLY_PROVIDERS: SocialNetworkSource[] = ['vodacom'];

/**
 * Map hostname -> list of allowed auth providers for sign-in/sign-up.
 * Domains not listed fall back to DEFAULT_PROVIDERS.
 */
export const allowedAuthProvidersByDomain: Record<string, SocialNetworkSource[]> =
  {
    'vc.trace.plus': VODACOM_ONLY_PROVIDERS,
    // MTN login is rendered by MtnOtpLogin, so no other provider is shown
    'gh-mtn.trace.plus': [],
  };

export const MTN_HOSTNAME = 'gh-mtn.trace.plus';

const MTN_TEST_KEY = 'mtn_domain_test';

/**
 * True on the MTN domain. Before the domain is mapped, MTN mode can be
 * tested on any host via ?mtnOnly=1 (kept for the tab session) or
 * GATSBY_MTN_DOMAIN_TEST=1.
 */
export const isMtnHostname = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.location.hostname.toLowerCase() === MTN_HOSTNAME) return true;
  if (process.env.GATSBY_MTN_DOMAIN_TEST === '1') return true;

  try {
    const mtnOnly = new URLSearchParams(window.location.search).get('mtnOnly');
    if (mtnOnly === '1') sessionStorage.setItem(MTN_TEST_KEY, '1');
    if (mtnOnly === '0') sessionStorage.removeItem(MTN_TEST_KEY);
    return sessionStorage.getItem(MTN_TEST_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Returns the list of allowed auth providers for the current hostname.
 * In SSR/build (no window), returns default providers. In browser, uses
 * hostname; supports dev override via ?vodacomOnly=1 or GATSBY_VODACOM_ONLY_DOMAIN_TEST=1.
 */
export function getAllowedAuthProviders(): SocialNetworkSource[] {
  if (typeof window === 'undefined') {
    return DEFAULT_PROVIDERS;
  }

  const url = new URL(window.location.href);
  const vodacomOnlyQuery = url.searchParams.get('vodacomOnly') === '1';
  const vodacomOnlyEnv =
    process.env.GATSBY_VODACOM_ONLY_DOMAIN_TEST === '1';
  if (vodacomOnlyQuery || vodacomOnlyEnv) {
    return VODACOM_ONLY_PROVIDERS;
  }

  if (isMtnHostname()) {
    return allowedAuthProvidersByDomain[MTN_HOSTNAME];
  }

  const hostname = window.location.hostname;
  return allowedAuthProvidersByDomain[hostname] ?? DEFAULT_PROVIDERS;
}
